import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { SphereMarker } from './SphereMarker';
import { Viewer } from '../viewer/viewer';
import { DefaultColors } from '../defines';
import { TextSprite } from '../TextSprite';
import { createHeightLabel } from './LabelUtils';
import { Utils } from '../utils';
import type { Subscription, Coordinate } from '@pointorama/potree-shared';
import {
  getAnnotationColorProperty,
  getAnnotationVisibleProperty,
  getStructurePolygonProperty,
  getStructureHeightProperty,
  getStructureKindProperty,
  getAnnotationShowLengthsProperty,
  getAnnotationShowHeightProperty,
  getFloorPlanePositionProperty,
  hasFloorPlanePosition,
} from '@pointorama/potree-shared';
import { StructureKind } from 'shared-types';
import type { EditableShape, EditableShapeKind, EditEdge } from '../personas/tools/implementations/line-tool/EditableShape';

/**
 * PointoramaStructure is a polygon-based annotation (beam or column) extruded
 * vertically by `structure_height`. Each base-ring vertex becomes a vertical
 * edge; the top ring mirrors the base. When height = 0, only the base ring is
 * drawn — the structure is a flat outline sitting on the containing room's floor.
 *
 * Rendering matches PointoramaRoom: Line2 + LineGeometry + LineMaterial for
 * all line work, SphereMarker on base-ring vertices only (top ring + vertical
 * edges are purely visual).
 */
export class PointoramaStructure extends THREE.Object3D implements EditableShape {
  private _identifier = '';
  groupName = '';
  groupIdentifier: string | null = null;
  selected = false;
  finished = false;
  boundingSphere?: THREE.Sphere;

  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    if (value) {
      this.loadReactiveProperties();
    }
  }

  private polygon: THREE.Vector3[] = [];
  private height: number;
  private kind: StructureKind;
  /** Base ring snapshot taken on enterEditMode, restored if the edit is cancelled. */
  private _editModeOriginalPolygon: THREE.Vector3[] | null = null;

  public spheres: SphereMarker[] = [];
  private baseLineGeometry: LineGeometry;
  private baseLine: Line2;
  public lineMaterial: LineMaterial;
  public backgroundLineMaterial: LineMaterial;
  public backgroundLine: Line2;

  // Top ring + vertical edges — only present when height > 0. The top ring is
  // a single closed-loop Line2; vertical edges are one Line2 per vertex so the
  // line shader never bridges across non-adjacent verticals.
  private topLineGeometry: LineGeometry | null = null;
  private topLine: Line2 | null = null;
  private verticalLines: { line: Line2; geometry: LineGeometry }[] = [];
  // Thick low-opacity glow lines for the top ring + vertical edges. They reuse the
  // top/vertical geometries and backgroundLineMaterial, so disposing the geometry is
  // handled by disposeExtrusion's existing geometry disposal — only the meshes need removing.
  private extrusionGlowLines: Line2[] = [];

  private color: THREE.Color;
  private viewer: Viewer;

  // Labels. Edge labels report the 2D length of each base-ring edge (length/width);
  // the height label reports the extrusion height, plus the beam's bottom elevation
  // above the floor for BEAM structures (same `↧` format windows use for the sill).
  private _showDistances = true;
  private _showHeight = true;
  public edgeLabels: TextSprite[] = [];
  public heightLabel: TextSprite;

  // Unit conversion (read from the viewer, mirrors PointoramaRoom).
  private lengthUnit?: { unitspermeter: number } | null;
  private lengthUnitDisplay?: { unitspermeter: number; code: string } | null;

  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _polygonSubscription?: Subscription;
  private _heightSubscription?: Subscription;
  private _kindSubscription?: Subscription;
  private _showLengthLabelsSubscription?: Subscription;
  private _showHeightSubscription?: Subscription;
  private _floorPosSubscription?: Subscription;
  private _subscribedFloorId?: string;

  constructor(viewer: Viewer, args: { polygon?: THREE.Vector3[]; height?: number; kind?: StructureKind; color?: THREE.Color } = {}) {
    super();

    this.viewer = viewer;
    this.polygon = (args.polygon ?? []).map((p) => p.clone());
    this.height = args.height ?? 0;
    this.kind = args.kind ?? StructureKind.COLUMN;
    this.color = args.color ?? DefaultColors.OBJECTS_THREE;

    this.baseLineGeometry = new LineGeometry();
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 1,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      transparent: true,
    });
    this.baseLine = new Line2(this.baseLineGeometry, this.lineMaterial);
    this.add(this.baseLine);

    this.backgroundLineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 15,
      resolution: new THREE.Vector2(1000, 1000),
      transparent: true,
      depthTest: false,
      opacity: 0.0,
    });
    this.backgroundLine = new Line2(this.baseLineGeometry, this.backgroundLineMaterial);
    this.backgroundLine.name = 'StructureBackgroundLine';
    this.add(this.backgroundLine);

    this.heightLabel = createHeightLabel();
    this.add(this.heightLabel);

    if (this.polygon.length >= 3) {
      this.rebuildVisuals();
    }
  }

  private coordToVec3(c: Coordinate): THREE.Vector3 {
    return new THREE.Vector3(c.x, c.y, c.z);
  }

  private loadReactiveProperties(): void {
    if (!this._identifier) return;

    try {
      this._colorSubscription?.unsubscribe();
      this._visibleSubscription?.unsubscribe();
      this._polygonSubscription?.unsubscribe();
      this._heightSubscription?.unsubscribe();
      this._kindSubscription?.unsubscribe();
      this._showLengthLabelsSubscription?.unsubscribe();
      this._showHeightSubscription?.unsubscribe();

      const polygonProp = getStructurePolygonProperty(this._identifier);
      const heightProp = getStructureHeightProperty(this._identifier);
      const kindProp = getStructureKindProperty(this._identifier);

      if (this.polygon.length === 0) {
        const coords = polygonProp.value;
        if (coords.length >= 3) this.polygon = coords.map((c) => this.coordToVec3(c));
      }
      this.height = heightProp.value;
      this.kind = kindProp.value;

      if (this.polygon.length >= 3) this.rebuildVisuals();

      this._polygonSubscription = polygonProp.subscribeToChanges((coords) => {
        if (coords.length >= 3) {
          this.polygon = coords.map((c) => this.coordToVec3(c));
          this.rebuildVisuals();
        }
      });
      this._heightSubscription = heightProp.subscribeToChanges((value) => {
        this.height = value;
        this.rebuildVisuals();
      });
      this._kindSubscription = kindProp.subscribeToChanges((value) => {
        this.kind = value;
        // Kind alone doesn't change geometry, but it flips the height label between
        // the plain height and the beam `h / ↧ bottom` form, so refresh the labels.
        this.updateLabels();
      });

      // --- Label visibility (shared with Distance/Area/Room via annotation_show_* identifiers) ---
      const lengthProp = getAnnotationShowLengthsProperty(this._identifier);
      this._showDistances = lengthProp.value;
      this._showLengthLabelsSubscription = lengthProp.subscribeToChanges((value) => {
        this._showDistances = value;
        this.updateLabels();
      });

      const heightLabelProp = getAnnotationShowHeightProperty(this._identifier);
      this._showHeight = heightLabelProp.value;
      this._showHeightSubscription = heightLabelProp.subscribeToChanges((value) => {
        this._showHeight = value;
        this.updateLabels();
      });

      // Apply the freshly-read toggle values (rebuildVisuals above ran with defaults).
      this.updateLabels();

      const colorProperty = getAnnotationColorProperty(this._identifier);
      this.setColor(new THREE.Color(colorProperty.value));
      this._colorSubscription = colorProperty.subscribeToChanges((value: string) => {
        this.setColor(new THREE.Color(value));
      });

      const visibleProperty = getAnnotationVisibleProperty(this._identifier);
      this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value: boolean) => {
        this.visible = value;
      });
    } catch {
      // Global property service not initialized yet.
    }
  }

  getPolygon(): THREE.Vector3[] {
    return this.polygon;
  }

  getHeight(): number {
    return this.height;
  }

  getKind(): EditableShapeKind {
    return 'structure';
  }

  getStructureKind(): StructureKind {
    return this.kind;
  }

  // ─── EditableShape ──────────────────────────────────────────────────

  enterEditMode(): EditEdge[] {
    // Snapshot the current base ring and hide the visuals while the edit overlay
    // (LineTool's editable handles) takes over — mirrors PointoramaOutline.
    this._editModeOriginalPolygon = this.polygon.map((p) => p.clone());
    this.setVisualsVisible(false);

    const edges: EditEdge[] = [];
    const n = this.polygon.length;
    for (let i = 0; i < n; i++) {
      const start = this.polygon[i];
      const end = this.polygon[(i + 1) % n];
      edges.push({
        start: start.clone(),
        end: end.clone(),
        midpoint: new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5),
        direction: new THREE.Vector3().subVectors(end, start).normalize(),
      });
    }
    return edges;
  }

  exitEditMode(confirm: boolean, newPolygon?: THREE.Vector3[]): void {
    if (confirm && newPolygon && newPolygon.length >= 3) {
      // Apply the edited XY ring but keep the existing base Z so the extrusion
      // (height + kind) is preserved — only the footprint changes.
      const baseZ = this.polygon[0]?.z ?? 0;
      this.polygon = newPolygon.map((p) => new THREE.Vector3(p.x, p.y, baseZ));
    } else if (this._editModeOriginalPolygon) {
      this.polygon = this._editModeOriginalPolygon.map((p) => p.clone());
    }
    this._editModeOriginalPolygon = null;
    // rebuildVisuals re-creates and re-shows all line work from the polygon.
    this.rebuildVisuals();
  }

  /** Toggle every line/sphere/label child's visibility (used to hide during edit mode). */
  private setVisualsVisible(visible: boolean): void {
    this.baseLine.visible = visible;
    this.backgroundLine.visible = visible;
    if (this.topLine) this.topLine.visible = visible;
    for (const { line } of this.verticalLines) line.visible = visible;
    for (const glow of this.extrusionGlowLines) glow.visible = visible;
    for (const s of this.spheres) s.visible = visible;
    for (const label of this.edgeLabels) label.visible = false;
    this.heightLabel.visible = false;
  }

  getBasePlane(): { position: THREE.Vector3 } {
    if (this.polygon.length === 0) return { position: new THREE.Vector3() };
    return { position: this.polygon[0].clone() };
  }

  // ─── Visuals ────────────────────────────────────────────────────────

  /**
   * Tear down and rebuild every visual element from the current
   * polygon/height/kind. Cheap enough to call on any property change.
   */
  private rebuildVisuals(): void {
    this.disposeSpheres();
    this.disposeExtrusion();
    this.disposeEdgeLabels();

    if (this.polygon.length < 3) return;

    // Anchor the Object3D at the first polygon vertex; all child geometry is
    // expressed relative to it to avoid float precision loss at large world
    // coordinates (same pattern as PointoramaRoom).
    this.position.copy(this.polygon[0]);

    // --- Base ring ---
    const baseVerts: number[] = [];
    for (const p of this.polygon) {
      baseVerts.push(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z);
      const sphere = new SphereMarker(this.viewer);
      sphere.setColor(this.color);
      sphere.setScaleFactor(this.viewer.scene.structureSphereScale);
      sphere.position.set(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z);
      this.spheres.push(sphere);
      this.add(sphere);
      // One edge label per base-ring edge (the closing edge included).
      this.addEdgeLabel();
    }
    // Close the loop back to the first vertex.
    const first = this.polygon[0];
    baseVerts.push(first.x - this.position.x, first.y - this.position.y, first.z - this.position.z);
    this.baseLineGeometry.dispose();
    if (baseVerts.length >= 6) {
      this.baseLineGeometry.setPositions(baseVerts);
      this.baseLine.computeLineDistances();
      this.backgroundLine.computeLineDistances();
      this.lineMaterial.needsUpdate = true;
      this.backgroundLineMaterial.needsUpdate = true;
    }
    this.baseLine.visible = true;
    this.backgroundLine.visible = true;

    // --- Top ring + vertical edges (only when height > 0) ---
    if (this.height > 0) {
      const topVerts: number[] = [];
      for (const p of this.polygon) {
        topVerts.push(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z + this.height);
      }
      topVerts.push(first.x - this.position.x, first.y - this.position.y, first.z - this.position.z + this.height);

      this.topLineGeometry = new LineGeometry();
      this.topLineGeometry.setPositions(topVerts);
      this.topLine = new Line2(this.topLineGeometry, this.lineMaterial);
      this.topLine.computeLineDistances();
      this.add(this.topLine);

      // Top-ring glow: shares backgroundLineMaterial so the whole object lights up
      // together when select() bumps that one opacity.
      const topGlow = new Line2(this.topLineGeometry, this.backgroundLineMaterial);
      topGlow.computeLineDistances();
      this.add(topGlow);
      this.extrusionGlowLines.push(topGlow);

      // Vertical edges: one Line2 per vertex. A single strip with "degenerate
      // returns" between verticals doesn't work — Line2 draws a continuous
      // thick polyline, so the jumps render as diagonal connectors across
      // non-adjacent verticals.
      for (let i = 0; i < this.polygon.length; i++) {
        const p = this.polygon[i];
        const bx = p.x - this.position.x;
        const by = p.y - this.position.y;
        const bz = p.z - this.position.z;
        const geometry = new LineGeometry();
        geometry.setPositions([bx, by, bz, bx, by, bz + this.height]);
        const line = new Line2(geometry, this.lineMaterial);
        line.computeLineDistances();
        this.add(line);
        this.verticalLines.push({ line, geometry });

        // Matching glow for this vertical edge (shares backgroundLineMaterial).
        const glow = new Line2(geometry, this.backgroundLineMaterial);
        glow.computeLineDistances();
        this.add(glow);
        this.extrusionGlowLines.push(glow);
      }
    }

    this.updateBoundingSphere();
    this.updateLabels();
  }

  private disposeSpheres(): void {
    this.spheres.forEach((s) => this.remove(s));
    this.spheres = [];
  }

  private disposeExtrusion(): void {
    // Glow lines reuse the top/vertical geometries (disposed below), so only remove
    // the meshes here to avoid double-disposing shared geometry.
    for (const glow of this.extrusionGlowLines) {
      this.remove(glow);
    }
    this.extrusionGlowLines = [];
    if (this.topLine) {
      this.remove(this.topLine);
      this.topLine = null;
    }
    if (this.topLineGeometry) {
      this.topLineGeometry.dispose();
      this.topLineGeometry = null;
    }
    for (const { line, geometry } of this.verticalLines) {
      this.remove(line);
      geometry.dispose();
    }
    this.verticalLines = [];
  }

  private updateBoundingSphere(): void {
    if (this.polygon.length === 0) {
      this.boundingSphere = undefined;
      return;
    }
    const origin = this.position;
    const pts = this.polygon.map((p) => p.clone().sub(origin));
    if (this.height > 0) {
      for (const p of this.polygon) {
        pts.push(new THREE.Vector3(p.x - origin.x, p.y - origin.y, p.z - origin.z + this.height));
      }
    }
    this.boundingSphere = new THREE.Box3().setFromPoints(pts).getBoundingSphere(new THREE.Sphere());
  }

  // ─── Labels ─────────────────────────────────────────────────────────

  /** Create an edge length label (styled to match PointoramaRoom's edge labels). */
  private addEdgeLabel(): void {
    const edgeLabel = new TextSprite('');
    edgeLabel.setBorderColor({ r: 0, g: 0, b: 0, a: 1.0 });
    edgeLabel.setBackgroundColor({ r: 0, g: 0, b: 0, a: 0.3 });
    edgeLabel.material.depthTest = false;
    edgeLabel.visible = false;
    edgeLabel.fontsize = 16;
    this.edgeLabels.push(edgeLabel);
    this.add(edgeLabel);
  }

  private disposeEdgeLabels(): void {
    this.edgeLabels.forEach((label) => this.remove(label));
    this.edgeLabels = [];
  }

  /** Format a model-space length into the user's display unit (mirrors PointoramaRoom). */
  private formatLength(value: number): string {
    let v = value;
    let suffix = '';
    if (this.lengthUnit != null && this.lengthUnitDisplay != null) {
      v = (v / this.lengthUnit.unitspermeter) * this.lengthUnitDisplay.unitspermeter;
      suffix = this.lengthUnitDisplay.code;
    }
    return `${v.toFixed(2)} ${suffix}`;
  }

  /**
   * Height label text. Columns show the bare extrusion height; beams additionally
   * show the elevation of their bottom above the floor, in the same two-line
   * `h … / ↧ …` format windows use for the sill.
   */
  private getHeightLabelText(): string {
    const heightValue = this.formatLength(this.height);
    if (this.kind !== StructureKind.BEAM) return heightValue;

    const bottom = this.getBeamBottomElevation();
    if (bottom == null) return heightValue;
    return `h ${heightValue}\n↧ ${this.formatLength(bottom)}`;
  }

  /** Raw export values: extrusion height, plus the beam's bottom elevation above the floor when defined. */
  getHeightValues(): number[] {
    if (this.kind !== StructureKind.BEAM) return [this.height];
    const bottom = this.getBeamBottomElevation();
    return bottom == null ? [this.height] : [this.height, bottom];
  }

  /**
   * Beam bottom elevation = base-ring Z (the beam's underside) minus the floor-plane
   * elevation of the floor group this structure belongs to. Returns null when no
   * floor exists, so the caller omits the elevation line. Mirrors OpeningAnnotation.
   */
  private getBeamBottomElevation(): number | null {
    const floorId = this.groupIdentifier;
    if (!floorId || this.polygon.length === 0) return null;
    try {
      if (!hasFloorPlanePosition(floorId)) return null;
      const floorZ = getFloorPlanePositionProperty(floorId).value?.z;
      if (typeof floorZ !== 'number') return null;
      this.ensureFloorSubscription(floorId);
      return this.polygon[0].z - floorZ;
    } catch {
      return null;
    }
  }

  /** Keep the beam bottom-elevation label live when the floor elevation is edited. */
  private ensureFloorSubscription(floorId: string): void {
    if (this._subscribedFloorId === floorId) return;
    this._floorPosSubscription?.unsubscribe();
    this._subscribedFloorId = floorId;
    try {
      this._floorPosSubscription = getFloorPlanePositionProperty(floorId).subscribeToChanges(() => {
        this.updateHeightLabelPosition();
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  /** Reposition + retext + reapply visibility for every label. */
  private updateLabels(): void {
    if (this._editModeOriginalPolygon) return; // labels stay hidden during edit
    for (let i = 0; i < this.edgeLabels.length; i++) this.updateEdgeLabelPosition(i);
    this.updateHeightLabelPosition();
  }

  private updateEdgeLabelPosition(index: number): void {
    const edgeLabel = this.edgeLabels[index];
    if (!edgeLabel || this.polygon.length < 2) return;

    const point = this.polygon[index];
    const nextPoint = this.polygon[(index + 1) % this.polygon.length];
    const origin = this.polygon[0];

    // Midpoint in coordinates relative to the origin to avoid precision loss.
    const center = point.clone().sub(origin).add(nextPoint.clone().sub(origin)).multiplyScalar(0.5);
    edgeLabel.position.copy(center);

    const distance = point.distanceTo(nextPoint);
    edgeLabel.setText(this.formatLength(distance));
    edgeLabel.visible = this._showDistances && distance > 0;
  }

  private updateHeightLabelPosition(): void {
    if (this.polygon.length < 3) {
      this.heightLabel.visible = false;
      return;
    }
    const origin = this.polygon[0];
    const centroid = new THREE.Vector3();
    for (const p of this.polygon) centroid.add(p.clone().sub(origin));
    centroid.divideScalar(this.polygon.length);
    // Sit the label in the middle of the extruded volume.
    centroid.z += this.height / 2;
    this.heightLabel.position.copy(centroid);

    this.heightLabel.visible = this._showHeight && this.height > 0;
    this.heightLabel.setText(this.getHeightLabelText());
  }

  /**
   * Per-frame label + line-material update driven by Viewer.rescaleLabelsForView.
   * Keeps labels at a constant screen size and the line resolution in sync.
   */
  updateScales(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number): void {
    const camera = cameraOverride ?? this.viewer.scene.getActiveCamera();

    let clientWidth: number, clientHeight: number;
    if (widthOverride !== undefined && heightOverride !== undefined) {
      clientWidth = widthOverride;
      clientHeight = heightOverride;
    } else {
      const size = this.viewer.renderer.getSize(new THREE.Vector2());
      clientWidth = size.width;
      clientHeight = size.height;
    }

    // Keep line materials' resolution current (nothing else drives this for structures).
    this.lineMaterial.resolution.set(clientWidth, clientHeight);
    this.backgroundLineMaterial.resolution.set(clientWidth, clientHeight);

    // Units can change at runtime; re-text the labels when they do.
    if (this.lengthUnit !== this.viewer.lengthUnit || this.lengthUnitDisplay !== this.viewer.lengthUnitDisplay) {
      this.lengthUnit = this.viewer.lengthUnit;
      this.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
      this.updateLabels();
    }

    const rescale = (label: TextSprite) => {
      const distance = camera.position.distanceTo(label.getWorldPosition(new THREE.Vector3()));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;
      label.scale.set(scale, scale, scale);
    };
    for (const label of this.edgeLabels) rescale(label);
    rescale(this.heightLabel);
  }

  setColor(color: THREE.Color): void {
    this.color = color;
    this.lineMaterial.color = color;
    this.backgroundLineMaterial.color = color;
    this.spheres.forEach((s) => s.setColor(color));
  }

  /** Called by the viewer's render loop; matches the PointoramaRoom contract. */
  update(): void {
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    this.lineMaterial.resolution.set(size.width, size.height);
    this.backgroundLineMaterial.resolution.set(size.width, size.height);
  }

  select(): void {
    this.selected = true;
    this.backgroundLineMaterial.opacity = 0.25;
  }

  deselect(): void {
    this.selected = false;
    this.backgroundLineMaterial.opacity = 0.0;
  }

  dispose(): void {
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._polygonSubscription?.unsubscribe();
    this._heightSubscription?.unsubscribe();
    this._kindSubscription?.unsubscribe();
    this._showLengthLabelsSubscription?.unsubscribe();
    this._showHeightSubscription?.unsubscribe();
    this._floorPosSubscription?.unsubscribe();

    this.disposeSpheres();
    this.disposeExtrusion();
    this.disposeEdgeLabels();
    this.remove(this.heightLabel);
    this.baseLineGeometry.dispose();
    this.lineMaterial.dispose();
    this.backgroundLineMaterial.dispose();
    this.remove(this.baseLine);
    this.remove(this.backgroundLine);
  }
}
